import { useState, useEffect } from 'react';
import { useForm } from 'react-hook-form';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { userApi } from '../../api/client';
import { useAuthStore } from '../../stores/authStore';
import { useApiQuery } from '../../hooks/useApiQuery';
import { Card, CardBody, CardHeader, Button, Input } from '../../components/ui';
import { User, Mail, Phone, Shield, Lock, Plus, Trash2, X, MapPin, Award } from 'lucide-react';
import { MALAYSIAN_STATES } from '../../lib/constants';

interface ProfileForm {
  full_name: string;
  contact_number: string;
  state: string;
}

export function ProfilePage() {
  const { user, updateUser } = useAuthStore();
  const queryClient = useQueryClient();
  const [success, setSuccess] = useState(false);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showAddGoogle, setShowAddGoogle] = useState(false);
  const [newGoogleEmail, setNewGoogleEmail] = useState('');
  const [newGoogleName, setNewGoogleName] = useState('');
  const [deleteConfirm, setDeleteConfirm] = useState<number | null>(null);

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isDirty },
  } = useForm<ProfileForm>({
    defaultValues: {
      full_name: user?.full_name || '',
      contact_number: user?.contact_number || '',
      state: user?.state || '',
    },
  });

  const { data: profileData } = useApiQuery({
    queryKey: ['profile'],
    queryFn: () => userApi.getProfile(),
    refetchOnMount: 'always',
  });

  // Use fresh profile data (has referral_code + created_at); fall back to auth store
  const profile = profileData || user;

  // Sync form values when fresh profile data loads
  useEffect(() => {
    if (profileData) {
      reset({
        full_name: profileData.full_name || '',
        contact_number: profileData.contact_number || '',
        state: profileData.state || '',
      });
    }
  }, [profileData, reset]);

  // Local Guide level (separate from the react-hook-form profile fields)
  const [lgLevel, setLgLevel] = useState<number>(user?.local_guide_level ?? 4);
  const [lgScreenshot, setLgScreenshot] = useState<File | null>(null);

  useEffect(() => {
    if (profileData?.local_guide_level) {
      setLgLevel(profileData.local_guide_level);
    }
  }, [profileData]);

  const lgMutation = useMutation({
    mutationFn: (formData: FormData) => userApi.submitLocalGuideLevel(formData),
    onSuccess: (response) => {
      if (response.error) { toast.error(response.error); return; }
      if (response.data) {
        updateUser(response.data);
        queryClient.invalidateQueries({ queryKey: ['profile'] });
        setLgScreenshot(null);
        toast.success(
          response.data.pending_local_guide_level
            ? 'Submitted for admin approval'
            : 'Local Guide level updated'
        );
      }
    },
  });

  const handleLgSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (lgLevel >= 6 && !lgScreenshot) {
      toast.error('Please upload a screenshot of your Local Guide profile for level 6 and above');
      return;
    }
    const fd = new FormData();
    fd.append('level', String(lgLevel));
    if (lgScreenshot) fd.append('screenshot', lgScreenshot);
    lgMutation.mutate(fd);
  };

  const mutation = useMutation({
    mutationFn: (data: ProfileForm) => userApi.updateProfile(data),
    onSuccess: (response) => {
      if (response.error) {
        toast.error(response.error);
        return;
      }
      if (response.data) {
        updateUser(response.data);
        setSuccess(true);
        setTimeout(() => setSuccess(false), 3000);
        toast.success('Profile updated');
      }
    },
  });

  const onSubmit = (data: ProfileForm) => {
    mutation.mutate(data);
  };

  const changePasswordMutation = useMutation({
    mutationFn: (data: { current_password: string; new_password: string }) =>
      userApi.changePassword(data),
    onSuccess: (response) => {
      if (response.error) {
        toast.error(response.error);
        return;
      }
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');
      toast.success('Password changed successfully');
    },
  });

  // Google accounts
  const { data: googleAccountsData } = useApiQuery({
    queryKey: ['google-accounts'],
    queryFn: () => userApi.getGoogleAccounts(),
    refetchOnMount: 'always',
  });
  const accounts = googleAccountsData?.accounts || [];

  const addGoogleMutation = useMutation({
    mutationFn: (data: { google_email: string; google_name?: string }) =>
      userApi.addGoogleAccount(data),
    onSuccess: (response) => {
      if (response.error) { toast.error(response.error); return; }
      queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
      setNewGoogleEmail('');
      setNewGoogleName('');
      setShowAddGoogle(false);
      toast.success('Google account added');
    },
  });

  const deleteGoogleMutation = useMutation({
    mutationFn: (id: number) => userApi.deleteGoogleAccount(id),
    onSuccess: (response) => {
      if (response.error) { toast.error(response.error); return; }
      queryClient.invalidateQueries({ queryKey: ['google-accounts'] });
      setDeleteConfirm(null);
      toast.success('Google account removed');
    },
  });

  const handleAddGoogle = (e: React.FormEvent) => {
    e.preventDefault();
    if (!newGoogleEmail.trim()) return;
    if (!newGoogleName.trim()) {
      toast.error('Display name is required so admins can verify your reviews');
      return;
    }
    addGoogleMutation.mutate({
      google_email: newGoogleEmail.trim(),
      google_name: newGoogleName.trim(),
    });
  };

  const handleChangePassword = (e: React.FormEvent) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.error('New password must be at least 6 characters');
      return;
    }
    if (newPassword !== confirmPassword) {
      toast.error('New passwords do not match');
      return;
    }
    changePasswordMutation.mutate({
      current_password: currentPassword,
      new_password: newPassword,
    });
  };

  return (
    <div className="max-w-2xl mx-auto space-y-6">
      <h1 className="text-2xl font-bold text-gray-900">Profile Settings</h1>

      <Card>
        <CardHeader>
          <div className="flex items-center space-x-3">
            <div className="w-12 h-12 bg-primary-600 rounded-full flex items-center justify-center">
              <span className="text-xl font-semibold text-white">
                {user?.full_name?.[0]?.toUpperCase() || 'U'}
              </span>
            </div>
            <div>
              <p className="font-semibold text-gray-900">{user?.full_name}</p>
              <p className="text-sm text-gray-500">{user?.email}</p>
            </div>
          </div>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
            {success && (
              <div className="p-3 bg-green-50 border border-green-200 rounded-lg text-sm text-green-600">
                Profile updated successfully!
              </div>
            )}

            {mutation.error && (
              <div className="p-3 bg-red-50 border border-red-200 rounded-lg text-sm text-red-600">
                Failed to update profile. Please try again.
              </div>
            )}

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="label flex items-center">
                  <User className="w-4 h-4 mr-2 text-gray-400" />
                  Full Name
                </label>
                <Input
                  {...register('full_name', {
                    required: 'Full name is required',
                    minLength: {
                      value: 2,
                      message: 'Name must be at least 2 characters',
                    },
                  })}
                  error={errors.full_name?.message}
                />
              </div>

              <div>
                <label className="label flex items-center">
                  <Mail className="w-4 h-4 mr-2 text-gray-400" />
                  Email
                </label>
                <Input value={user?.email || ''} disabled className="bg-gray-50" />
                <p className="mt-1 text-xs text-gray-500">Email cannot be changed</p>
              </div>

              <div>
                <label className="label flex items-center">
                  <Phone className="w-4 h-4 mr-2 text-gray-400" />
                  Contact Number
                </label>
                <Input
                  {...register('contact_number')}
                  placeholder="e.g., 012-345 6789"
                />
                <p className="mt-1 text-xs text-gray-500">
                  Payouts are made to your Touch 'n Go wallet linked to this number. Please make sure it's correct and also reachable on WhatsApp for any updates.
                </p>
              </div>

              <div>
                <label className="label flex items-center">
                  <MapPin className="w-4 h-4 mr-2 text-gray-400" />
                  State
                </label>
                <select
                  {...register('state', { required: 'Please select your state' })}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  <option value="">Select your state…</option>
                  {MALAYSIAN_STATES.map((s) => (
                    <option key={s} value={s}>{s}</option>
                  ))}
                </select>
                {errors.state && (
                  <p className="mt-1 text-sm text-red-600">{errors.state.message}</p>
                )}
              </div>

              <div>
                <label className="label flex items-center">
                  <Shield className="w-4 h-4 mr-2 text-gray-400" />
                  Role
                </label>
                <Input
                  value={user?.role === 'admin' ? 'Administrator' : 'Participant'}
                  disabled
                  className="bg-gray-50 capitalize"
                />
              </div>
            </div>

            <div className="flex justify-end">
              <Button type="submit" loading={mutation.isPending} disabled={!isDirty}>
                Save Changes
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-gray-900">Account Statistics</h2>
        </CardHeader>
        <CardBody>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Credit Balance</p>
              <p className="text-2xl font-bold text-primary-600">
                {profile?.credit_balance ?? 0}
              </p>
            </div>
            <div className="p-4 bg-gray-50 rounded-lg">
              <p className="text-sm text-gray-600">Member Since</p>
              <p className="text-lg font-semibold text-gray-900">
                {profile?.created_at
                  ? new Date(profile.created_at).toLocaleDateString()
                  : 'N/A'}
              </p>
            </div>
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardHeader className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center">
            <Award className="w-4 h-4 mr-2" />
            Google Local Guide Level
          </h2>
          {profile?.pending_local_guide_level ? (
            <span className="text-xs font-medium px-2 py-1 rounded-full bg-yellow-100 text-yellow-800">
              Pending verification (L{profile.pending_local_guide_level})
            </span>
          ) : null}
        </CardHeader>
        <CardBody>
          <form onSubmit={handleLgSubmit} className="space-y-4">
            <p className="text-sm text-gray-600">
              Current level: <strong>L{profile?.local_guide_level ?? 4}</strong>. Levels 1–5 apply
              immediately. Levels 6 and above require a screenshot and admin approval.
            </p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">Your Local Guide Level</label>
                <select
                  value={lgLevel}
                  onChange={(e) => setLgLevel(parseInt(e.target.value, 10))}
                  className="w-full rounded-lg border border-gray-300 px-3 py-2 text-sm focus:border-primary-500 focus:ring-primary-500"
                >
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map((n) => (
                    <option key={n} value={n}>Level {n}</option>
                  ))}
                </select>
              </div>
              {lgLevel >= 6 && (
                <div>
                  <label className="label">Proof Screenshot</label>
                  <input
                    type="file"
                    accept="image/*"
                    onChange={(e) => setLgScreenshot(e.target.files?.[0] ?? null)}
                    className="w-full text-sm text-gray-600 file:mr-3 file:py-2 file:px-3 file:rounded-lg file:border-0 file:bg-primary-50 file:text-primary-700"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Upload a screenshot of your Google Local Guide profile showing your level.
                  </p>
                </div>
              )}
            </div>
            <div className="flex justify-end">
              <Button type="submit" loading={lgMutation.isPending}>
                {lgLevel >= 6 ? 'Submit for approval' : 'Save level'}
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>


      {/* Linked Google Accounts */}
      <Card>
        <CardHeader className="flex items-center justify-between">
          <h2 className="font-semibold text-gray-900 flex items-center">
            <Mail className="w-4 h-4 mr-2" />
            Linked Google Accounts
          </h2>
          {!showAddGoogle && (
            <Button size="sm" variant="secondary" onClick={() => setShowAddGoogle(true)}>
              <Plus className="w-4 h-4 mr-1" />
              Add Account
            </Button>
          )}
        </CardHeader>
        <CardBody className="space-y-3">
          {showAddGoogle && (
            <form onSubmit={handleAddGoogle} className="p-3 bg-gray-50 rounded-lg border border-gray-200 space-y-3">
              <div>
                <label className="label">Gmail / Google Email *</label>
                <Input
                  type="email"
                  value={newGoogleEmail}
                  onChange={(e) => setNewGoogleEmail(e.target.value)}
                  placeholder="example@gmail.com"
                  required
                />
              </div>
              <div>
                <label className="label">Display Name *</label>
                <Input
                  value={newGoogleName}
                  onChange={(e) => setNewGoogleName(e.target.value)}
                  placeholder="Name shown on your Google profile"
                  required
                />
                <p className="mt-1 text-xs text-gray-500">
                  This is the name admins will see on your reviews. Use the exact name shown on your Google profile so we can verify your work.
                </p>
              </div>
              <div className="flex justify-end space-x-2">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => { setShowAddGoogle(false); setNewGoogleEmail(''); setNewGoogleName(''); }}
                  type="button"
                >
                  Cancel
                </Button>
                <Button size="sm" type="submit" loading={addGoogleMutation.isPending} disabled={!newGoogleEmail.trim() || !newGoogleName.trim()}>
                  Save
                </Button>
              </div>
            </form>
          )}

          {accounts.length === 0 && !showAddGoogle ? (
            <p className="text-sm text-gray-500 text-center py-4">
              No Google accounts linked. Add your Google account so admins can verify your reviews.
            </p>
          ) : (
            <div className="divide-y divide-gray-100">
              {accounts.map((account) => (
                <div key={account.id} className="flex items-center justify-between py-2.5">
                  <div className="flex items-center gap-3">
                    <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center">
                      <Mail className="w-4 h-4 text-blue-600" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-gray-900">{account.google_email}</p>
                      {account.google_name && (
                        <p className="text-xs text-gray-500">{account.google_name}</p>
                      )}
                    </div>
                  </div>
                  {deleteConfirm === account.id ? (
                    <div className="flex items-center space-x-1">
                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() => deleteGoogleMutation.mutate(account.id)}
                        loading={deleteGoogleMutation.isPending}
                      >
                        Confirm
                      </Button>
                      <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(null)}>
                        <X className="w-4 h-4" />
                      </Button>
                    </div>
                  ) : (
                    <Button variant="secondary" size="sm" onClick={() => setDeleteConfirm(account.id)} title="Remove">
                      <Trash2 className="w-4 h-4 text-red-500" />
                    </Button>
                  )}
                </div>
              ))}
            </div>
          )}
        </CardBody>
      </Card>

      <Card>
        <CardHeader>
          <h2 className="font-semibold text-gray-900 flex items-center">
            <Lock className="w-4 h-4 mr-2" />
            Change Password
          </h2>
        </CardHeader>
        <CardBody>
          <form onSubmit={handleChangePassword} className="space-y-4">
            <div>
              <label className="label">Current Password</label>
              <Input
                type="password"
                value={currentPassword}
                onChange={(e) => setCurrentPassword(e.target.value)}
                placeholder="Enter current password"
                required
              />
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="label">New Password</label>
                <Input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  placeholder="At least 6 characters"
                  required
                />
              </div>
              <div>
                <label className="label">Confirm New Password</label>
                <Input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  placeholder="Re-enter new password"
                  required
                />
              </div>
            </div>
            <div className="flex justify-end">
              <Button
                type="submit"
                loading={changePasswordMutation.isPending}
                disabled={!currentPassword || !newPassword || !confirmPassword}
              >
                Change Password
              </Button>
            </div>
          </form>
        </CardBody>
      </Card>
    </div>
  );
}
